import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuthClient } from '../auth-context.tsx'
import { sessionKeys } from './keys.ts'

/**
 * A desktop app's sign-in (OAuth device authorization, Phase 12e): ForShow
 * shows a code and opens id.forhim.io/device; the signed-in person sees which
 * app is asking and approves or denies. ForShow's polling then receives (or is
 * refused) a session.
 */

/** Apps that may sign in this way, by their client id. */
export const DEVICE_APPS: Record<string, string> = { forshow: 'ForShow' }

export type DeviceRequest =
  { kind: 'pending'; app: string } | { kind: 'settled'; approved: boolean; status: string }

/** Codes are shown in capitals with no spaces; people type them however. */
export const normalizeUserCode = (code: string) => code.replace(/[\s-]/g, '').toUpperCase()

export function useDeviceRequest(userCode: string) {
  const authClient = useAuthClient()
  return useQuery({
    queryKey: sessionKeys.device(userCode),
    enabled: userCode.length > 0,
    retry: false,
    queryFn: async (): Promise<DeviceRequest> => {
      const { data, error } = await authClient.device({ query: { user_code: userCode } })
      if (error || !data) throw new Error(error?.error_description || 'That code is not valid.')
      // better-auth 1.6 names no client here; only listed apps can ask (DEVICE_CLIENTS in
      // @church/auth), and today that is ForShow alone. A code another account opened first
      // is refused when settled.
      const { status, client_id: clientId } = data as { status: string; client_id?: string }
      if (status !== 'pending') return { kind: 'settled', approved: status === 'approved', status }
      return { kind: 'pending', app: DEVICE_APPS[clientId ?? 'forshow'] ?? 'A desktop app' }
    },
  })
}

export function useSettleDevice() {
  const authClient = useAuthClient()
  return useMutation({
    mutationFn: async ({ userCode, approve }: { userCode: string; approve: boolean }) => {
      const { error } = approve
        ? await authClient.device.approve({ userCode })
        : await authClient.device.deny({ userCode })
      if (error) throw new Error(error.error_description || 'Could not settle the request')
      return approve
    },
  })
}
